import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { iconChevronLeft } from '@sorare/core/src/atoms/icons/system/iconChevronLeft.ts';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { SecondaryPageBackground } from '@sorare/core/src/atoms/ui/SecondaryPageBackground';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { playableBlockchainRarities } from '@sorare/core/src/lib/cards';
import { PillSwitchRarity } from '@sorare/marketplace/src/atoms/PillSwitchRarity';

import { ProMissionsRarityBalances } from '#src/components/missions/ProMissionsRarityBalances/index.tsx';
import { SecondaryPageHeader } from '#src/pages/PlayMissionsPageLayout/SecondaryPageHeader/index.tsx';

const Root = styled.div`
  position: relative;
  isolation: isolate;
  min-height: var(--100vh);
`;

const StyledSBHorizontal = styled(SBHorizontal)`
  padding: var(--unit) 0;
`;

const BottomBar = styled.div`
  position: fixed;
  bottom: max(
    var(--double-unit),
    env(safe-area-inset-bottom, var(--double-unit))
  );
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
`;

type Props = {
  children: ReactNode;
  rarity: Rarity;
  categoryIcon?: ReactNode;
  categoryTitle: ReactNode;
  goBack: () => void;
  onSelectRarity: (rarity: Rarity) => void;
  unclaimedTasksByRarity?: { [k in Rarity]?: number };
  overlayMidColor?: string;
};

export const ProMissionsPageLayout = ({
  children,
  rarity,
  categoryIcon,
  categoryTitle,
  goBack,
  onSelectRarity,
  unclaimedTasksByRarity,
  overlayMidColor = '#30D63E',
}: Props) => {
  const { sport } = useSportContext();
  const { lifecycle, update } = useLifecycle();

  return (
    <Root>
      <SecondaryPageBackground overlayMidColor={overlayMidColor} />
      <Container>
        <StyledSBHorizontal>
          <IconButton
            size="medium"
            color="quaternary"
            icon={iconChevronLeft}
            onClick={goBack}
          />
          <ProMissionsRarityBalances rarity={rarity} />
        </StyledSBHorizontal>
        <Vertical gap={3}>
          <SecondaryPageHeader
            icon={categoryIcon}
            title={categoryTitle}
            subtitle={
              <FormattedMessage defaultMessage="Get rewarded by playing daily" />
            }
          />
          {children}
        </Vertical>
      </Container>
      <BottomBar>
        <PillSwitchRarity
          rarities={playableBlockchainRarities.map(R => ({
            value: R as Rarity,
            badgeContent: unclaimedTasksByRarity?.[R],
          }))}
          selectedRarity={rarity}
          viewTransitionPrefix="rarity-picker"
          onChange={R => {
            update(LIFECYCLE.missionsRarityFilter, {
              ...lifecycle?.missionsRarityFilter,
              [sport]: R,
            });
            onSelectRarity(R);
          }}
        />
      </BottomBar>
    </Root>
  );
};
